import { LinkButton, PrimaryButton } from "@digipost/dds-buttons-react";
import { Card } from "@digipost/dds-card-react";
import { Heading } from "@digipost/dds-heading-react";
import { Language, useLanguage } from "@digipost/translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC } from "react";

import { DigipostEntryPoint } from "../../../types/entrypoint";
import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/siteimprove";
import Chatbot from "../chatbot/Chatbot";
import { useEntrypointContext } from "../EntrypointContext";
import Translated from "../infrastructure/components/Translated";
import { getStoreLink } from "../infrastructure/device-util";
import { useRootPath } from "../infrastructure/hooks/useRootPath";
import { useTranslate } from "../infrastructure/hooks/useTranslate";
import { findURIWithWindowHostname } from "../infrastructure/link";
import styles from "./PersonLogin.module.less";

const PersonLogin: FC = () => {
  const entrypoint: DigipostEntryPoint = useEntrypointContext();
  const rootPath = useRootPath();
  const { language } = useLanguage();

  const returnUrl = "/innlogging/person/complete";
  const returnUrlError = `${rootPath}/loginError`;

  const translate = useTranslate();

  const doAuth = (provider: string): void => {
    const url = new URL(
      findURIWithWindowHostname("login_idp", entrypoint.link),
    );
    window.location.href =
      url +
      `?provider=${provider}&eidasLoa=SUBSTANTIAL&rolle=PRIVAT&returnUrl=${returnUrl}&returnUrlError=${returnUrlError}&lang=${language}`;
  };

  return (
    <>
      <article>
        <div className={styles.outerContainer}>
          <div className={styles.loginContent}>
            <div>
              <Card
                className={styles.cardWithImage}
                imageSrc={
                  "/innlogging/images/illustrations/alt_paa_ett_sted.svg"
                }
                imageAlt={translate(
                  "login.image_description.phone_with_letters",
                )}
                heading={translate("login.digipost")}
                headingLevel={1}
                headingSize={"large"}
              >
                <Translated textKey={"login.digipost_description"} />
                <PrimaryButton
                  className={styles.fullWidthButton}
                  iconLeft={<FontAwesomeIcon icon={["far", "lock"]} />}
                  onClick={() => {
                    siteImproveEvent({
                      action: SiteImproveActions.BUTTON,
                      category: SiteImproveCategories.LOGIN_PRIVATE_USER,
                      label: "knapp til idporten for å logge inn",
                    });
                    doAuth("idporten");
                  }}
                >
                  <Translated textKey={"login.generic"} />
                </PrimaryButton>
              </Card>
              <div className={styles.urls}>
                <p>
                  <Translated textKey={"login.dont_have_an_account"} />{" "}
                  <LinkButton
                    as={"a"}
                    className={styles.inlineLinkButton}
                    onClick={() => {
                      siteImproveEvent({
                        action: SiteImproveActions.LINK,
                        category: SiteImproveCategories.LOGIN_PRIVATE_USER,
                        label: "link for å registrere ny bruker",
                      });
                    }}
                    href={`/registrering${"/" + language}/person`}
                  >
                    <Translated textKey="login.register_here" />
                  </LinkButton>
                </p>
                <p>
                  <Translated textKey={"login.limited_login"} />
                  {": "}
                  <LinkButton
                    onClick={() => {
                      siteImproveEvent({
                        action: SiteImproveActions.BUTTON,
                        category: SiteImproveCategories.LOGIN_PRIVATE_USER,
                        label: "knapp til signicat for å logge inn",
                      });
                      doAuth("signicat");
                    }}
                    className={styles.inlineLinkButton}
                  >
                    {translate("login.logg_inn_via_signicat")}
                  </LinkButton>
                </p>
              </div>
            </div>

            <aside className={styles.cardDigipostApp}>
              <img
                src={"/innlogging/images/illustrations/superhund.svg"}
                alt={translate("login.image_description.dog_flying")}
              />
              <section className={styles.cardContent}>
                <Heading size={"small"} level={2} className={styles.headings}>
                  <Translated textKey={"login.tired_of_logging_in.title"} />
                </Heading>
                <Translated textKey={"login.tired_of_logging_in.description"} />
                <LinkButton
                  as={"a"}
                  href={getStoreLink()}
                  iconRight={
                    <FontAwesomeIcon icon={["far", "arrow-up-right"]} />
                  }
                  onClick={() => {
                    siteImproveEvent({
                      action: SiteImproveActions.LINK,
                      category: SiteImproveCategories.MOBILEAPP,
                      label:
                        "link for å komme til appstore/playstore for mobilapp",
                    });
                  }}
                >
                  <Translated textKey={"login.tired_of_logging_in.url"} />
                </LinkButton>
              </section>
            </aside>
            <aside className={styles.cardDoYouNeedHelp}>
              <img
                src={"/innlogging/images/illustrations/person_i_telefon.svg"}
                alt={translate("login.image_description.man_talking")}
              />
              <section className={styles.cardContent}>
                <Heading size={"small"} level={2} className={styles.headings}>
                  <Translated textKey={"login.do_you_need_help.title"} />
                </Heading>
                <Translated textKey={"login.do_you_need_help.description"} />
                <LinkButton
                  as="a"
                  href={getHelpSitesPath(language)}
                  iconLeft={<FontAwesomeIcon icon={["far", "arrow-right"]} />}
                >
                  <Translated textKey={"login.do_you_need_help.url"} />
                </LinkButton>
              </section>
            </aside>
          </div>
          <Chatbot />
        </div>
      </article>
    </>
  );
};

function getHelpSitesPath(language: Language): string {
  switch (language) {
    case "nb":
      return "/privat/hjelp-og-kontakt/innlogging";
    case "nn":
      return "/nn/privat/hjelp-og-kontakt/innlogging";
    case "en":
      return "/en/consumer/help-and-contact/login";
  }
}

export default PersonLogin;
